import { SupportCodeCard } from "../components/SupportAccess";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, AUTH } from "../lib/api";
import { useMe, useOrg } from "../lib/session";
import { arrange, defaultLayout, layoutFor, useCompany, usePrefs, type Company, type Entry } from "../lib/prefs";
import { ErrorText, Field, Loading, PageHeader } from "../components/ui";
import { HELP_EMAIL, useNavItems, type NavItem } from "../components/Layout";
import type { Brand } from "../lib/brand";
import type { Dashboard as DashboardData } from "../lib/types";
import { useEntitlements } from "../lib/plans";
import { Icon } from "../components/icons";
import { UnlockBar } from "../components/Unlock";
import { CustomerMessages } from "./automations";
import { toast } from "../components/Toast";

/** Settings: the company's details (and, for owners, closing the company).
 *  Everything else has its own page in the settings menu. */
export function Settings() {
  const company = useCompany();
  const { orgId } = useOrg();
  return (
    <>
      <PageHeader title="Company" />
      {company.isLoading ? <Loading /> : company.data ? <CompanyCard company={company.data} /> : <ErrorText error={company.error} />}
      <CustomerMessages />
      <p className="muted small">Need help? <a href={`mailto:${HELP_EMAIL}`}>Message {HELP_EMAIL}</a></p>
      {company.data?.is_owner && <CloseCompanyCard name={company.data.display_name} orgId={orgId} />}
    </>
  );
}

/** SidebarSettings: each person's own sidebar order, folders and hidden pages. */
export function SidebarSettings() {
  const items = useNavItems();
  return (
    <>
      <PageHeader title="Sidebar" />
      <SidebarCard items={items} />
      <HiddenTabsCard items={items} />
    </>
  );
}

/** UsageSettings: how much of the plan this month has used. */
export function UsageSettings() {
  return (
    <>
      <PageHeader title="Plan & usage" />
      <PlanUsageCard />
    </>
  );
}

/** SupportSettings: one-time codes for SVENEX support. */
export function SupportSettings() {
  return (
    <>
      <PageHeader title="SVENEX support access" />
      <SupportCodeCard />
    </>
  );
}

function CompanyCard({ company }: { company: Company }) {
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const [c, setC] = useState(company);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => setC(company), [company]);
  const ro = !company.can_edit;
  const set = (patch: Partial<Company>) => { setC({ ...c, ...patch }); setSaved(false); };
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const out = await api<Company>(`${base}/company`, { method: "PUT", json: {
        display_name: c.display_name, website: c.website || null, phone: c.phone || null, public_email: c.public_email || null, address: c.address || null,
        dashboard_brand_color: c.dashboard_brand_color,
      } });
      qc.setQueryData([orgId, "company"], out);
      await qc.invalidateQueries({ queryKey: ["me"] });
      setSaved(true);
      toast("Company details saved");
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const uploadLogo = async (file: File) => {
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api<{ logo: string }>(`${base}/brand/logo`, { method: "POST", body: fd });
      const cur = await api<{ brand: Brand }>(`${base}/brand`);
      await api(`${base}/brand`, { method: "PUT", json: { brand: { ...cur.brand, logo: up.logo } } });
      await qc.invalidateQueries({ queryKey: [orgId] });
    } catch (e) { setError(e); }
  };
  return (
    <section className="card">
      <h2>Company</h2>
      {ro ? <p className="muted small">Only admins can change these.</p> : <UnlockBar />}
      <div className="company-head">
        {company.logo_url ? <img className="company-logo" src={company.logo_url} alt={`${company.display_name} logo`} /> : <div className="company-logo empty" aria-hidden>{company.display_name.slice(0, 1)}</div>}
        {allows("brand") && (
          <label className="button-link secondary-file">
            {company.logo_url ? "Change icon" : "Upload icon"}
            <input type="file" accept="image/png,image/jpeg" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} />
          </label>
        )}
        <span className="muted small">Shown in the sidebar, the chat widget and the booking page. PNG or JPG, up to 2 MB.</span>
      </div>
      <div className="row">
        <Field label="Company name"><input value={c.display_name} maxLength={120} disabled={ro} onChange={(e) => set({ display_name: e.target.value })} /></Field>
        <Field label="Legal name (on your contract)"><input value={c.legal_name} disabled /></Field>
      </div>
      <div className="row">
        <Field label="Website"><input value={c.website ?? ""} maxLength={200} placeholder="https://www.example.com" disabled={ro} onChange={(e) => set({ website: e.target.value })} /></Field>
        <Field label="Phone"><input value={c.phone ?? ""} maxLength={32} placeholder="+973 1700 0000" disabled={ro} onChange={(e) => set({ phone: e.target.value })} /></Field>
        <Field label="Public email"><input value={c.public_email ?? ""} maxLength={254} placeholder="hello@example.com" disabled={ro} onChange={(e) => set({ public_email: e.target.value })} /></Field>
      </div>
      <Field label="Address"><textarea rows={2} maxLength={300} value={c.address ?? ""} disabled={ro} onChange={(e) => set({ address: e.target.value })} /></Field>
      <label className="check"><input type="checkbox" checked={c.dashboard_brand_color} disabled={ro} onChange={(e) => set({ dashboard_brand_color: e.target.checked })} /> Use our brand colour for the dashboard's buttons</label>
      <ErrorText error={error} />
      {!ro && <div className="inline"><button disabled={busy} onClick={save}>{busy ? "Saving…" : "Save company details"}</button>{saved && <span className="muted small">Saved.</span>}</div>}
    </section>
  );
}

const newID = () => Math.random().toString(36).slice(2, 10).replace(/[^a-z0-9]/g, "x");

/** toEntries turns the current arrangement into a full, editable layout. */
function toEntries(items: NavItem[], layout: Entry[]): Entry[] {
  const out: Entry[] = arrange(items, layout).map((e) => ("item" in e ? { item: e.item.key } : { folder: { ...e.folder, items: e.items.map((i) => i.key) } }));
  // Keep empty folders the person made, so they can fill them.
  for (const e of layout) if (e.folder && !out.some((x) => x.folder?.id === e.folder!.id)) out.push({ folder: { ...e.folder, items: [] } });
  return out;
}

function SidebarCard({ items: all }: { items: NavItem[] }) {
  const { prefs, loaded, save } = usePrefs();
  const hidden = prefs.hidden ?? [];
  // Settings is fixed at the bottom of the sidebar, so it isn't arranged here.
  const items = all.filter((i) => i.key !== "settings" && !hidden.includes(i.key));
  const [draft, setDraft] = useState<Entry[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [folderName, setFolderName] = useState("");
  const hiddenKey = hidden.join(",");
  useEffect(() => { if (loaded) setDraft(toEntries(items, layoutFor(prefs.sidebar, items))); }, [loaded, hiddenKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const hide = async (k: string) => {
    setError(null);
    try { await save({ ...prefs, sidebar: draft ?? prefs.sidebar, hidden: [...hidden, k] }); } catch (e) { setError(e); }
  };
  if (!draft) return null;
  const label = (k: string) => items.find((i) => i.key === k)?.label ?? k;
  const folders = draft.filter((e) => e.folder).map((e) => e.folder!);
  const change = (next: Entry[]) => { setDraft(next); setSaved(false); };
  const move = <T,>(list: T[], i: number, d: number) => { const j = i + d; if (j < 0 || j >= list.length) return list; const c = [...list]; [c[i], c[j]] = [c[j], c[i]]; return c; };
  const without = (k: string): Entry[] => draft.filter((e) => e.item !== k).map((e) => (e.folder ? { folder: { ...e.folder, items: e.folder.items.filter((x) => x !== k) } } : e));
  const putIn = (k: string, fid: string) => {
    const rest = without(k);
    change(fid ? rest.map((e) => (e.folder?.id === fid ? { folder: { ...e.folder, items: [...e.folder.items, k] } } : e)) : [...rest, { item: k }]);
  };
  const folderOf = (k: string) => folders.find((f) => f.items.includes(k))?.id ?? "";
  const picker = (k: string) => (
    <select aria-label={`Folder for ${label(k)}`} value={folderOf(k)} onChange={(e) => putIn(k, e.target.value)}>
      <option value="">No folder</option>
      {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
    </select>
  );
  return (
    <section className="card">
      <h2>Order and folders</h2>
      <p className="muted small">Put the pages in the order you like, group them into folders, or hide the ones you don't use. Just for you; pages your role can't open never show.</p>
      <ol className="sidebar-editor">
        {draft.map((e, i) => e.item ? (
          <li key={e.item}>
            <span className="grow">{label(e.item)}</span>
            {picker(e.item)}
            <button type="button" className="secondary small" aria-label={`Move ${label(e.item)} up`} disabled={i === 0} onClick={() => change(move(draft, i, -1))}>↑</button>
            <button type="button" className="secondary small" aria-label={`Move ${label(e.item)} down`} disabled={i === draft.length - 1} onClick={() => change(move(draft, i, 1))}>↓</button>
            {e.item !== "settings" && <button type="button" className="secondary small" onClick={() => hide(e.item!)}>Hide</button>}
          </li>
        ) : (
          <li key={e.folder!.id} className="folder">
            <div className="folder-row">
              <span className="nav-ico folder-ico" aria-hidden><Icon name="folder" /></span>
              <input className="grow" aria-label="Folder name" value={e.folder!.name} maxLength={30} onChange={(ev) => change(draft.map((x) => (x.folder?.id === e.folder!.id ? { folder: { ...x.folder, name: ev.target.value } } : x)))} />
              <button type="button" className="secondary small" aria-label={`Move folder ${e.folder!.name} up`} disabled={i === 0} onClick={() => change(move(draft, i, -1))}>↑</button>
              <button type="button" className="secondary small" aria-label={`Move folder ${e.folder!.name} down`} disabled={i === draft.length - 1} onClick={() => change(move(draft, i, 1))}>↓</button>
              <button type="button" className="link small" onClick={() => {
                const at = draft.indexOf(e);
                change([...draft.slice(0, at), ...e.folder!.items.map((k) => ({ item: k })), ...draft.slice(at + 1)]);
              }}>Remove folder</button>
            </div>
            {e.folder!.items.length === 0 ? <p className="muted small">Empty. Choose this folder for a page above or below.</p> : (
              <ol>
                {e.folder!.items.map((k, j) => (
                  <li key={k}>
                    <span className="grow">{label(k)}</span>
                    {picker(k)}
                    <button type="button" className="secondary small" aria-label={`Move ${label(k)} up`} disabled={j === 0} onClick={() => change(draft.map((x) => (x.folder?.id === e.folder!.id ? { folder: { ...x.folder, items: move(x.folder.items, j, -1) } } : x)))}>↑</button>
                    <button type="button" className="secondary small" aria-label={`Move ${label(k)} down`} disabled={j === e.folder!.items.length - 1} onClick={() => change(draft.map((x) => (x.folder?.id === e.folder!.id ? { folder: { ...x.folder, items: move(x.folder.items, j, 1) } } : x)))}>↓</button>
                    {k !== "settings" && <button type="button" className="secondary small" onClick={() => hide(k)}>Hide</button>}
                  </li>
                ))}
              </ol>
            )}
          </li>
        ))}
      </ol>
      <form className="inline" onSubmit={(ev) => { ev.preventDefault(); const n = folderName.trim(); if (!n || folders.length >= 10) return; change([...draft, { folder: { id: newID(), name: n, items: [], collapsed: false } }]); setFolderName(""); }}>
        <input aria-label="New folder name" placeholder="New folder name, e.g. Sales" maxLength={30} value={folderName} onChange={(e) => setFolderName(e.target.value)} />
        <button type="submit" className="secondary" disabled={!folderName.trim() || folders.length >= 10}>Add folder</button>
      </form>
      <ErrorText error={error} />
      <div className="inline" style={{ marginTop: ".75rem" }}>
        <button onClick={async () => { setError(null); try { await save({ ...prefs, sidebar: draft }); setSaved(true); } catch (e) { setError(e); } }}>Save sidebar</button>
        <button className="link" onClick={async () => { setError(null); try { await save({ ...prefs, sidebar: [] }); setDraft(toEntries(items, defaultLayout(items))); setSaved(true); } catch (e) { setError(e); } }}>Back to the default order</button>
        {saved && <span className="muted small">Saved.</span>}
      </div>
    </section>
  );
}

/** HiddenTabsCard lists the pages someone hid from their sidebar. */
function HiddenTabsCard({ items }: { items: NavItem[] }) {
  const { prefs, save } = usePrefs();
  const [error, setError] = useState<unknown>(null);
  const hidden = items.filter((i) => (prefs.hidden ?? []).includes(i.key));
  return (
    <section className="card" id="hidden-tabs">
      <h2 className="card-title">Hidden tabs {hidden.length > 0 && <span className="count">{hidden.length}</span>}</h2>
      {hidden.length === 0 ? <p className="muted small">Nothing hidden. Use Hide above to tidy away pages you don't use.</p> : (
        <ul className="hidden-tabs">
          {hidden.map((i) => (
            <li key={i.key}>
              <span className="nav-ico"><Icon name={i.key} /></span>
              <Link to={i.to}>{i.label}</Link>
              <span className="spacer" />
              <button className="secondary small" onClick={() => save({ ...prefs, hidden: (prefs.hidden ?? []).filter((k) => k !== i.key) }).catch(setError)}>Show in sidebar</button>
            </li>
          ))}
        </ul>
      )}
      <ErrorText error={error} />
    </section>
  );
}

/** PlanUsageCard shows how much of the plan this month has used (admins). */
function PlanUsageCard() {
  const { base, orgId, allows } = useOrg();
  const plan = useEntitlements();
  const summary = useQuery({ queryKey: [orgId, "dashboard"], queryFn: () => api<DashboardData>(`${base}/dashboard`), enabled: allows("team") });
  const usage = summary.data?.usage;
  if (!allows("team")) return null;
  if (summary.isLoading) return <Loading />;
  if (!usage || usage.length === 0) return <section className="card"><p className="muted small">Nothing used yet this month.</p><ErrorText error={summary.error} /></section>;
  return (
    <section className="card">
      <h2>This month{plan.data?.plan_name && <span className="muted small"> · {plan.data.plan_name}</span>}</h2>
      <div className="usage-list">
        {usage.map((u) => {
          const pct = u.limit ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
          return (
            <div key={u.metric} className="usage-row">
              <div className="usage-head"><span>{u.label}</span><span className="num">{u.used.toLocaleString()} <span className="muted">/ {u.limit == null ? "unlimited" : u.limit.toLocaleString()}</span></span></div>
              {u.limit != null && <div className={`progress-bar${pct >= 90 ? " hot" : ""}`} aria-hidden><span style={{ width: `${pct}%` }} /></div>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

interface SecurityInfo { mfa: { enabled: boolean } }

/** ConfirmFields asks for the password and, when the app is on, a code. */
export function ConfirmFields() {
  const sec = useQuery({ queryKey: ["account-security"], queryFn: () => api<SecurityInfo>(`${AUTH}/security`) });
  return (
    <>
      <Field label="Your password (leave empty if you only sign in with Google)"><input name="password" type="password" autoComplete="current-password" maxLength={256} /></Field>
      {sec.data?.mfa.enabled && <Field label="Code from your authenticator app"><input name="code" inputMode="numeric" autoComplete="one-time-code" maxLength={32} required /></Field>}
    </>
  );
}

function CloseCompanyCard({ name, orgId }: { name: string; orgId: string }) {
  const qc = useQueryClient();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <section className="card danger-zone">
      <h2>Close {name}</h2>
      <p className="small">Closing stops everything at once: nobody can sign in to it, and its AI agents, booking page and channels stop. You can reopen it for 30 days. After that, the company and all its data (customers, conversations, bookings) are deleted for good.</p>
      {!open ? <button className="danger" onClick={() => setOpen(true)}>Close this company…</button> : (
        <form className="form" onSubmit={async (e) => {
          e.preventDefault();
          const d = new FormData(e.currentTarget);
          setBusy(true); setError(null);
          try {
            await api(`/v1/orgs/${orgId}/closure`, { method: "POST", json: { confirm: String(d.get("confirm") ?? ""), password: String(d.get("password") ?? ""), code: String(d.get("code") ?? "") } });
            await qc.invalidateQueries({ queryKey: ["me"] });
          } catch (err) { setError(err); } finally { setBusy(false); }
        }}>
          <Field label={`Type the company name to confirm: ${name}`}><input name="confirm" autoComplete="off" required maxLength={130} /></Field>
          <ConfirmFields />
          <p className="muted small">Owners and admins of {name} get an email. Signed in as {me.data?.user.email}.</p>
          <ErrorText error={error} />
          <div className="inline">
            <button type="submit" className="danger" disabled={busy}>{busy ? "Closing…" : `Close ${name}`}</button>
            <button type="button" className="link" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
